import React, { useEffect, useState, useRef } from "react";
import { api, fmtDay, fmtDate, gmapsNavUrl, wazeNavUrl } from "@/lib/api";
import { Link } from "react-router-dom";
import { Truck, MapPin, Navigation, Camera, CheckCircle2, Calendar, UtensilsCrossed } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function DriverDashboardPage() {
  const [deliveries, setDeliveries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liveTracking, setLiveTracking] = useState(false);
  const liveRef = useRef(null);
  const deliveriesRef = useRef([]);

  const load = async () => {
    setLoading(true);
    const [d, o] = await Promise.all([api.get("/deliveries"), api.get("/orders")]);
    setDeliveries(d.data); setOrders(o.data);
    deliveriesRef.current = d.data;
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const pingAll = (silent = false) => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const active = deliveriesRef.current.filter((x) => !["returned_warehouse"].includes(x.status));
      for (const d of active) {
        try {
          await api.post(`/deliveries/${d.id}/gps`, { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
        } catch (e) {
          console.warn(`GPS ping failed for delivery ${d.id}:`, e?.response?.data?.detail || e?.message);
        }
      }
      if (!silent) toast.success(`Pinged ${active.length} active deliveries`);
    }, () => { if (!silent) toast.error("Could not get location"); });
  };

  const toggleLiveTracking = () => {
    if (liveTracking) {
      clearInterval(liveRef.current);
      liveRef.current = null;
      setLiveTracking(false);
      toast.info("Live tracking stopped");
    } else {
      pingAll(true);
      liveRef.current = setInterval(() => pingAll(true), 30000);
      setLiveTracking(true);
      toast.success("Live tracking started — location shared every 30s");
    }
  };
  useEffect(() => () => { if (liveRef.current) clearInterval(liveRef.current); }, []);

  const active = deliveries.filter((d) => !["returned_warehouse"].includes(d.status));

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto" data-testid="driver-dashboard">
      <div className="mb-6">
        <div className="label-eyebrow">My Schedule</div>
        <h1 className="font-display text-3xl md:text-4xl mt-2">Driver Dashboard</h1>
      </div>

      <Button onClick={toggleLiveTracking} data-testid="live-tracking-all"
        className={`w-full h-12 mb-2 rounded-sm ${liveTracking ? "bg-red-600 hover:bg-red-500 text-white" : "bg-[#D4AF37] text-black hover:bg-[#E5C86B]"}`}>
        <Navigation className={`w-4 h-4 mr-2 ${liveTracking ? "animate-pulse" : ""}`} />
        {liveTracking ? "Stop Live Tracking" : "Start Live Tracking (auto every 30s)"}
      </Button>
      <Button onClick={() => pingAll()} data-testid="ping-all" variant="outline" className="w-full border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] h-10 mb-6 rounded-sm">
        <Navigation className="w-4 h-4 mr-2" /> Ping Once to All Active Trips
      </Button>
      {liveTracking && <div className="text-[10px] text-emerald-400 -mt-4 mb-5 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Sharing location every 30 seconds — keep this page open</div>}

      <div className="mb-3 label-eyebrow">Active ({active.length})</div>
      <div className="space-y-3">
        {loading && <div className="text-zinc-600 text-sm text-center py-8">Loading…</div>}
        {!loading && active.length === 0 && (
          <div className="card-lux p-8 text-center">
            <Truck className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
            <h3 className="font-display text-xl">No active deliveries</h3>
            <p className="text-zinc-500 text-sm mt-1">Check back later or contact Operations.</p>
          </div>
        )}
        {active.map((d) => {
          const order = orders.find((o) => o.id === d.order_id);
          return (
            <Link key={d.id} to={`/delivery/${d.id}`} className="card-lux p-5 block" data-testid={`driver-trip-${d.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="font-display text-xl">{order?.customer_name || "—"}</div>
                <div className="flex flex-col items-end gap-1.5">
                  <span className="stage-chip text-[10px]" style={{ color: "#D4AF37", borderColor: "#D4AF3755" }}>{d.status.replace(/_/g, " ")}</span>
                  {order && <StatusBadge status={order.status} />}
                </div>
              </div>
              <div className="text-xs text-zinc-500 flex items-center gap-1.5 mb-1"><MapPin className="w-3 h-3" /> {order?.event_location}</div>
              <div className="text-xs text-zinc-500 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {order ? fmtDate(order.event_date) : "—"}</div>
              {order?.event_location && (
                <div className="flex gap-2 mt-2">
                  <button type="button" data-testid={`nav-gmaps-${d.id}`}
                     onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(gmapsNavUrl(order), "_blank"); }}
                     className="flex-1 text-center text-[11px] px-2 py-1.5 rounded-sm border border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
                    Google Maps
                  </button>
                  <button type="button" data-testid={`nav-waze-${d.id}`}
                     onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(wazeNavUrl(order), "_blank"); }}
                     className="flex-1 text-center text-[11px] px-2 py-1.5 rounded-sm border border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
                    Waze
                  </button>
                </div>
              )}
              {(order?.items || []).length > 0 && (
                <div className="mt-3 pt-3 border-t border-[#1a1a1a]" data-testid={`trip-dishes-${d.id}`}>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-600 mb-1.5 flex items-center gap-1.5"><UtensilsCrossed className="w-3 h-3" /> Load check — {order.items.length} dishes</div>
                  <div className="space-y-0.5 max-h-36 overflow-y-auto pr-1">
                    {order.items.map((it, i) => (
                      <div key={`${it.name}-${i}`} className="flex items-baseline justify-between gap-2 text-xs">
                        <span className="text-zinc-300 truncate">
                          {it.name}
                          {it.notes && <span className="text-[#D4AF37]"> ※ {it.notes}</span>}
                        </span>
                        <span className="text-zinc-500 shrink-0">{it.quantity} {it.uom || "pcs"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#1a1a1a] text-xs">
                <span className="text-zinc-500">Loading {((d.loading_checklist || []).filter((i) => i.checked).length)}/{(d.loading_checklist || []).length}</span>
                <span className="text-[#D4AF37]">Open trip →</span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
